import { computed } from 'vue';
import { useStore } from 'vuex';
import {
  areDocumentUploadsRequired as checkDocumentUploadsRequired,
  areDocumentUploadsHidden as checkDocumentUploadsHidden,
} from '@/utils/document-upload-requirements';
import {
  BusinessVerification,
  VerificationService,
} from '@clearbanc/service-classes/verification-service';
import { FileUpload } from '@/types/file-upload.types';

/**
 * Composable for handling document upload requirements and verification logic
 * Note: Verification and uploads data is now fetched globally when the user lands on the site.
 * @returns Object containing reactive document upload requirements and verification state
 */
export function useDocumentUploads() {
  const store = useStore();

  const isLexisNexisDDPEnabled = computed<boolean>(() => {
    return store.getters.isLexisNexisDDPEnabled;
  });

  const businessVerification = computed<BusinessVerification>(() => {
    return store.getters.businessVerification;
  });

  const uploads = computed<FileUpload[]>(() => {
    return store.getters.uploads;
  });

  const areDocumentUploadsRequired = computed(() => {
    return checkDocumentUploadsRequired({
      isLexisNexisDDPEnabled: isLexisNexisDDPEnabled.value,
      businessVerification: businessVerification.value,
      businessUploads: uploads.value,
    });
  });

  const areDocumentUploadsHidden = computed(() => {
    return checkDocumentUploadsHidden({
      isLexisNexisDDPEnabled: isLexisNexisDDPEnabled.value,
      businessVerification: businessVerification.value,
      businessUploads: uploads.value,
    });
  });

  const hasPassedBusinessVerification = computed(() => {
    return VerificationService.hasPassedBusinessVerification(
      businessVerification.value,
    );
  });

  const hasBusinessUploadedRequiredVerificationDocs = computed(() => {
    return VerificationService.hasBusinessUploadedRequiredVerificationDocs(
      uploads.value as any,
    );
  });

  const hasUserUploadedRequiredVerificationDocs = computed(() => {
    return VerificationService.hasUserUploadedRequiredVerificationDocs(
      uploads.value as any,
      store.getters.userId,
    );
  });

  const hasSoftFailedBusinessVerification = computed(() => {
    return VerificationService.hasSoftFailedBusinessVerification(
      businessVerification.value,
    );
  });

  const hasHardFailedBusinessVerification = computed(() => {
    return VerificationService.hasHardFailedBusinessVerification(
      businessVerification.value,
    );
  });

  return {
    areDocumentUploadsRequired,
    areDocumentUploadsHidden,
    hasPassedBusinessVerification,
    hasBusinessUploadedRequiredVerificationDocs,
    hasUserUploadedRequiredVerificationDocs,
    hasSoftFailedBusinessVerification,
    hasHardFailedBusinessVerification,
  };
}
